/***** Shipping Protection – add-once, cleanup, plan-by-interval mirroring, add-alongside (Recharge-safe) *****/

const PROTECTION_VARIANT_ID = 52457174040939; // your protection variant id
const PROTECTION_PROP_KEY = "_auto_added";
const PROTECTION_PROP_VAL = "shipping_protection";
const SESSION_FLAG = "shipping_protection_offered";

// ---- tiny utils ----
const root = () => (window.Shopify?.routes?.root) || "/";
const getCart = () => fetch(root() + "cart.js", {headers:{Accept:"application/json"}}).then(r=>r.json());
const post  = (p,b) => fetch(root()+p, {method:"POST", headers:{"Content-Type":"application/json",Accept:"application/json"}, body:JSON.stringify(b)}).then(r=>r.json());
const setOffered = ()=>{ try{sessionStorage.setItem(SESSION_FLAG,"1");}catch(e){} };
const wasOffered = ()=>{ try{return sessionStorage.getItem(SESSION_FLAG)==="1";}catch(e){return false;} };
const clearOffered = ()=>{ try{sessionStorage.removeItem(SESSION_FLAG);}catch(e){} };

const isProtection = (it)=> it.variant_id===PROTECTION_VARIANT_ID || (it.properties && it.properties[PROTECTION_PROP_KEY]===PROTECTION_PROP_VAL);
const hasProtection = (cart)=> cart.items.some(isProtection);
const hasAnyNonProtection = (cart)=> cart.items.some(i=>!isProtection(i));

// ---- interval <-> plan helpers ----
function normalizeUnit(u){
  return (u||'').toLowerCase().replace(/s$/,''); // singularize
}
// Build a key like "1-month" from a selling_plan-like object (cart exposes allocation.selling_plan)
function planKeyFromSellingPlan(sp){
  if (!sp) return null;
  // Try parsing name first (works for Recharge/Shopify defaults: "Deliver every 1 month")
  if (sp.name){
    const m = String(sp.name).toLowerCase().match(/(\d+)\s*(day|week|month|year)/);
    if (m) return `${m[1]}-${normalizeUnit(m[2])}`;
  }
  // Fallbacks (in case custom objects are present)
  const n = sp.delivery_interval || sp.interval || null;
  const u = normalizeUnit(sp.delivery_interval_unit || sp.interval_unit || null);
  if (n && u) return `${n}-${u}`;
  return null;
}

// First non-protection subscription interval key in the cart
function desiredPlanKeyFromCart(cart){
  for (const it of cart.items){
    if (isProtection(it)) continue;
    const sp = it.selling_plan_allocation?.selling_plan || it.selling_plan || null;
    const key = planKeyFromSellingPlan(sp);
    if (key) return key;
  }
  return null;
}

// Protection plan id for a given interval key, using the map injected via Liquid
function protectionPlanIdFor(key){
  // If the prebuilt map exists, use it
  const map = window.SHIPPING_PROTECTION_PLAN_MAP || {};
  if (key && map[key]) return map[key];

  // As a fallback, try to build a map from window.SHIPPING_PROTECTION_PLANS (if present)
  const plans = window.SHIPPING_PROTECTION_PLANS || [];
  for (const p of plans){
    const k = planKeyFromSellingPlan({ name: p.name });
    if (k === key) return p.id;
  }
  return null;
}

// ---- cart mutations (safe) ----
async function removeProtectionByKey(cart){
  const prot = cart.items.find(isProtection);
  if (!prot) return;
  await post("cart/change.js", { id: prot.key, quantity: 0 });
}
const addProtOneTime   = ()=> post("cart/add.js", { id: PROTECTION_VARIANT_ID, quantity:1, properties:{[PROTECTION_PROP_KEY]:PROTECTION_PROP_VAL} });
const addProtWithPlan  = (sp)=> post("cart/add.js", { id: PROTECTION_VARIANT_ID, quantity:1, selling_plan: sp, properties:{[PROTECTION_PROP_KEY]:PROTECTION_PROP_VAL} });

// ---- reconciliation (single source of truth) ----
let RECONCILING = false;
let lastRun = 0;
let lastMutationAt = 0;
let lastPlanToggleAt = 0;
let BYPASS_CLEANUP_UNTIL = 0;

const COOLDOWN_MS = 450;
const MUTATION_GRACE_MS = 900;
const PLAN_TOGGLE_GRACE_MS = 1400;

function markMutation(){ lastMutationAt = Date.now(); }
function markPlanToggle(){ lastPlanToggleAt = Date.now(); }
function bypassCleanup(ms){ BYPASS_CLEANUP_UNTIL = Date.now() + ms; }
function withinGrace(){
  const now = Date.now();
  return (now - lastMutationAt < MUTATION_GRACE_MS) || (now - lastPlanToggleAt < PLAN_TOGGLE_GRACE_MS) || (now < BYPASS_CLEANUP_UNTIL);
}

async function reconcile(){
  if (RECONCILING) return;
  const now = Date.now();
  if (now - lastRun < COOLDOWN_MS) return;

  RECONCILING = true;
  try {
    let cart = await getCart();

    // reset session if empty
    if (cart.item_count === 0){ clearOffered(); return; }

    // 1) ensure add-once on first non-empty (default one-time)
    if (!wasOffered() && hasAnyNonProtection(cart) && !hasProtection(cart)){
      await addProtOneTime();
      setOffered();
      cart = await getCart();
    } else if (!wasOffered() && hasProtection(cart)) {
      setOffered();
    }

    // 2) auto-cleanup: never leave protection as sole item
    if (hasProtection(cart) && !hasAnyNonProtection(cart)){
      if (withinGrace()){
        await new Promise(r=>setTimeout(r, 300));
        const confirm = await getCart();
        if (!(hasProtection(confirm) && !hasAnyNonProtection(confirm))) return;
        cart = confirm;
      }
      await removeProtectionByKey(cart);
      const updated = await getCart();
      if (updated.item_count === 0) clearOffered();
      return;
    }

    // 3) mirror plan by interval when settled
    const desiredKey = desiredPlanKeyFromCart(cart);        // e.g., "1-month"
    const desiredId  = protectionPlanIdFor(desiredKey);     // protection's own plan id (may be null)
    const prot = cart.items.find(isProtection);
    if (!prot) return;

    const currentPlanId = prot.selling_plan_allocation?.selling_plan?.id || prot.selling_plan || null;

    if (desiredKey && desiredId && currentPlanId !== desiredId){
      markMutation();
      await removeProtectionByKey(cart);
      try { await addProtWithPlan(desiredId); }
      catch(e){ console.warn("SP: add with plan failed, fallback one-time", e); await addProtOneTime(); }
      return;
    }

    if (!desiredKey && currentPlanId){
      markMutation();
      await removeProtectionByKey(cart);
      await addProtOneTime();
      return;
    }
  } catch (e){
    console.warn("SP reconcile error:", e);
  } finally {
    lastRun = Date.now();
    RECONCILING = false;
  }
}

// ---- scheduler + burst polling ----
let timer;
function scheduleReconcile(delay=220){
  clearTimeout(timer);
  timer = setTimeout(reconcile, delay);
}
let pollTimer, pollsLeft=0;
function burstPoll(){
  clearInterval(pollTimer);
  pollsLeft = 8; // ~4s total
  pollTimer = setInterval(()=>{
    if (pollsLeft-- <= 0){ clearInterval(pollTimer); return; }
    reconcile();
  }, 500);
}

// ---- read plan selection near a UI element (radio/select/option text) -> "N-unit" ----
function findPlanKeyNear(el){
  const scope = el.closest('form, .recharge-cart, [data-cart-drawer], .cart-drawer, #CartDrawer') || document;

  // radios with data
  const r = scope.querySelector('input[name="selling_plan"]:checked');
  if (r && (r.dataset.deliveryInterval || r.dataset.interval) && (r.dataset.deliveryIntervalUnit || r.dataset.unit)){
    return `${r.dataset.deliveryInterval || r.dataset.interval}-${normalizeUnit(r.dataset.deliveryIntervalUnit || r.dataset.unit)}`;
  }

  // selects with data on option
  const sel = scope.querySelector('select[name="selling_plan"], select[name*="selling_plan"]');
  if (sel?.selectedOptions?.[0]){
    const opt = sel.selectedOptions[0];
    const n = opt.dataset.deliveryInterval || opt.dataset.interval || (opt.value.match(/(\d+)/)||[])[1];
    const unitCandidate = opt.dataset.deliveryIntervalUnit || opt.dataset.unit || opt.textContent;
    if (n && unitCandidate){
      return `${n}-${normalizeUnit(unitCandidate)}`;
    }
  }

  // last resort: read visible text near controls
  const freq = scope.querySelector('.recharge-cart__frequency, [data-recharge-frequency]');
  if (freq){
    const m = freq.textContent.toLowerCase().match(/(\d+)\s*(day|week|month|year)/);
    if (m) return `${m[1]}-${normalizeUnit(m[2])}`;
  }

  return null;
}

// ---- add alongside using an interval key ----
async function addProtectionAlongsideFromKey(intervalKey){
  // translate interval -> protection plan id
  const planId = protectionPlanIdFor(intervalKey);
  try {
    bypassCleanup(2000);
    markPlanToggle(); markMutation();

    let cart = await getCart();
    const prot = cart.items.find(isProtection);
    const protPlanId = prot ? (prot.selling_plan_allocation?.selling_plan?.id || prot.selling_plan || null) : null;

    if (!planId){
      // No matching plan on protection; ensure one-time
      if (!prot) { await addProtOneTime(); setOffered(); }
      return scheduleReconcile(400);
    }

    if (!prot){
      await addProtWithPlan(planId);
      setOffered();
      return;
    }
    if (protPlanId !== planId){
      await removeProtectionByKey(cart);
      await addProtWithPlan(planId);
      setOffered();
    }
  } catch (e){
    console.warn("SP addProtectionAlongsideFromKey error:", e);
  } finally {
    scheduleReconcile(400);
    burstPoll();
  }
}

// ---- event wiring ----

// 1) DOM ready
document.addEventListener("DOMContentLoaded", ()=>scheduleReconcile(0));

// 2) Recharge/Rebuy events (use if present)
document.addEventListener("recharge:cart:updated", ()=>{ markMutation(); scheduleReconcile(150); });
document.addEventListener("rebuy:cart.change",   ()=>{ markMutation(); scheduleReconcile(150); });

// 3) Global add hooks (click/submit). If a plan is visible, add protection with that interval immediately.
document.addEventListener("click", (e)=>{
  const t = e.target.closest('button, a, input[type="submit"]');
  if (!t) return;

  const key = findPlanKeyNear(t);
  if (key) addProtectionAlongsideFromKey(key);

  // Still reconcile (covers one-time adds)
  if (
    t.matches('[name="add"]') ||
    t.matches('[data-add-to-cart]') ||
    t.matches('[data-action="add-to-cart"]') ||
    t.matches('.product-form__submit') ||
    t.matches('.quick-add__submit') ||
    t.matches('[data-rebuy-id*="add"]') ||
    t.matches('[data-recharge*="add"]')
  ){
    markMutation();
    scheduleReconcile(250);
    burstPoll();
  }
}, true);

document.addEventListener("submit", (e)=>{
  const f = e.target;
  if (!(f instanceof HTMLFormElement)) return;

  const key = findPlanKeyNear(f);
  if (key) addProtectionAlongsideFromKey(key);

  if (f.action && f.action.includes('/cart/add')){
    markMutation();
    scheduleReconcile(250);
    burstPoll();
  }
}, true);

// 4) Plan UI changes (radio/select) -> add alongside + reconcile
document.addEventListener("change", (e)=>{
  const el = e.target;
  if (!(el instanceof HTMLElement)) return;

  if (
    el.matches('input[name="selling_plan"]') ||
    el.matches('select[name="selling_plan"], select[name*="selling_plan"]') ||
    el.closest('.rc-selling-plans, [data-recharge-selling-plans], .recharge-cart__frequency, [data-recharge-frequency]')
  ){
    const key = findPlanKeyNear(el);
    if (key) addProtectionAlongsideFromKey(key);
    markPlanToggle(); markMutation();
    scheduleReconcile(350);
    burstPoll();
  }
}, true);

// 5) “Upgrade to subscription” buttons (by text/context) -> treat like plan toggle
function normText(el){ return (el?.textContent || "").trim().toLowerCase().replace(/\s+/g,' '); }
function looksLikeUpgradeBtn(btn){
  const t = normText(btn);
  const patterns = [/upgrade.*subscription/, /switch.*subscription/, /subscribe.*save/, /make.*subscription/, /start.*subscription/];
  if (patterns.some(rx => rx.test(t))) return true;
  if (t.includes("subscription")) return true;
  if (btn.matches('[data-recharge*="upgrade"], [data-recharge*="subscription"]')) return true;
  if (btn.closest('.recharge-cart, [data-recharge-selling-plans], .recharge-cart__frequency')) return true;
  return false;
}
document.addEventListener("click", (e)=>{
  const btn = e.target.closest('button,[role="button"]');
  if (!btn) return;
  if (looksLikeUpgradeBtn(btn)){
    const key = findPlanKeyNear(btn);
    if (key) addProtectionAlongsideFromKey(key);
    else { bypassCleanup(2000); markPlanToggle(); markMutation(); }
    scheduleReconcile(350);
    burstPoll();
  }
}, true);

// 6) Drawer observer (safety net)
(function observeDrawer(){
  const attach = ()=>{
    const sel = '.recharge-cart, .rebuy-cart, [data-cart-drawer], .cart-drawer, #CartDrawer';
    const el = document.querySelector(sel) || document.body;
    const mo = new MutationObserver(()=> { markMutation(); scheduleReconcile(200); });
    mo.observe(el, { childList:true, subtree:true, attributes:true });
    scheduleReconcile(0);
  };
  if (document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', attach, {once:true});
  } else {
    attach();
  }
})();